import { isArray, cloneDeep } from 'lodash'
import moment from 'moment'

// Observation

export const observationIcons = {
    projectObservationId: 'fa-hashtag',
    hasExtendedVisibility: 'fa-megaphone',
    visibility: 'fa-eye',
    status: 'fa-square',
    priority: 'fa-flag',
    severity: 'fa-exclamation',
    cost: 'fa-dollar-sign',
    archived: 'fa-archive',
    dueStatus: 'fa-circle',
    dueWarning: 'fa-calendar',
    due: 'fa-calendar',
    happenedAt: 'fa-calendar',
    createdAt: 'fa-calendar',
    publishedAt: 'fa-calendar',
    closedAt: 'fa-calendar',
    updatedAt: 'fa-calendar',
    ageOrClosingTime: 'fa-clock',
    numComments: 'fa-comment',
    numWatchers: 'fa-eye',
    numAttachments: 'fa-paperclip',
    reporter: 'fa-user',
    closedBy: 'fa-user',
    attachments: 'fa-paperclip',
    mainAttachment: 'fa-paperclip',
    category: 'fa-tag',
    cause: 'fa-clipboard',
    location: 'fa-location',
    locationDrawing: 'fa-map-marker-exclamation',
    relatedCompanies: 'fa-building',
    relatedCompany: 'fa-building',
    contractors: 'fa-building',
    sharedWith: 'fa-users',
    restrictedTo: 'fa-users',
    responsibleUser: 'fa-user',
    responsibleLeader: 'fa-user',
}

export const visibilityIcons = {
    restricted: 'fa-user-shield',
}

export const observationLimits = {
    title: {
        min: 0,
        max: 50,
    },
    latitude: {
        min: -90,
        max: 90,
    },
    longitude: {
        min: -180,
        max: 180,
    },
}

export const statusColor = {
    open: '#2276b2',
    inProgress: '#FFC107',
    closed: '#4caf50',
}

export const statusEnum = Object.keys(statusColor)

export const severityColor = {
    positive: '#42b371',
    minor: '#2685e3',
    major: '#FF5533',
    critical: '#881600',
}

export const severityEnum = Object.keys(severityColor)

export const priorityColor = {
    low: '#2685e3',
    medium: '#FFA630',
    high: '#FF5533',
    urgent: '#881600',
}

export const priorityEnum = Object.keys(priorityColor)

export const dueStatusColor = {
    ok: '#2276b2',
    dueSoon: '#ffa000',
    overdue: '#C44536',
}

export const dueStatusEnum = Object.keys(dueStatusColor)

export const visibility = ['private', 'shared', 'restricted', 'public']

// Observation Sorting

export const observationSort = {
    ageOrClosingTime: null,
    category: null,
    cause: null,
    cost: null,
    createdAt: null,
    dueStatus: null,
    happenedAt: null,
    location: null,
    priority: null,
    projectObservationId: null,
    severity: null,
    status: null,
    updatedAt: null,
}

export function setObservationSort(sorting: Record<string, any>) {
    const copyObservationSort = cloneDeep(observationSort)
    Object.keys(copyObservationSort).map((key) => {
        if (sorting[key] != null) {
            copyObservationSort[key] = sorting[key]
        }
    })
    return copyObservationSort
}

// Observation Filters
export const observationSingleValues = ['reporterIsNull', 'archived', 'watching', 'reporter']

export const observationArrayValues = [
    'visibility',
    'status',
    'priority',
    'severity',
    'dueStatus',
    'project',
    'category',
    'categoryName',
    'cause',
    'causeName',
    'location',
    'locationName',
    'responsibleUser',
    'responsibleLeader',
    // 'reporterCompany',
    //'reporterCompanyRole',
    'contractors',
    'relatedCompanies',
]

export const observationObjectValues = [
    'createdAt',
    'happenedAt',
    'publishedAt',
    'updatedAt',
    'closedAt',
    'cost',
]

export const observationFilter = {
    project: [],
    group: null,
    reporter: null,
    reporterIsNull: null,
    visibility: [],
    status: [], //
    priority: [],
    severity: [],
    dueStatus: [],
    archived: null,
    category: [],
    categoryName: [],
    cause: [],
    causeName: [],
    location: [],
    locationName: [],
    searchTerm: null,
    watching: null,
    responsibleUser: [],
    responsibleLeader: [],
    reporterCompany: [],
    reporterCompanyRole: [],
    relatedCompanies: [],
    contractors: [],
    createdAt: {
        ge: null,
        le: null,
        period: null,
    },
    happenedAt: {
        ge: null,
        le: null,
        period: null,
    },
    publishedAt: {
        ge: null,
        le: null,
        period: null,
    },
    updatedAt: {
        ge: null,
        le: null,
        period: null,
    },
    closedAt: {
        ge: null,
        le: null,
        period: null,
    },
    cost: {
        ge: null,
        le: null,
    },
}

export function setObservationFilter(filters: Record<string, any>) {
    const copyObservationFilter = cloneDeep(observationFilter)

    Object.keys(copyObservationFilter).map((key) => {
        if (key == 'searchTerm') {
            copyObservationFilter[key] = filters[key]
        } else if (observationSingleValues.includes(key)) {
            if (!isArray(filters[key]) && filters[key] != null) {
                copyObservationFilter[key] = filters[key]
            }
        } else if (observationArrayValues.includes(key)) {
            if (filters[key] && isArray(filters[key])) {
                if (filters[key]?.length > 0) {
                    copyObservationFilter[key] = filters[key]
                }
            } else {
                if (filters[key] != null) {
                    copyObservationFilter[key] = [filters[key]]
                }
            }
        } else if (observationObjectValues.includes(key)) {
            if (filters[key] && !isArray(filters[key])) {
                Object.keys(filters[key]).map((timeKey) => {
                    if (filters[key][timeKey] != null) {
                        copyObservationFilter[key][timeKey] = filters[key][timeKey]
                    }
                })
            }
        }
    })
    return copyObservationFilter
}

export function formatObservationModuleFe(feSettings: string | null) {
    const observationModule = {
        meMode: false,
        sort: observationSort,
        filter: observationFilter,
    }

    if (feSettings) {
        const feSettingsJson = JSON.parse(feSettings)

        if (Object.prototype.hasOwnProperty.call(feSettingsJson, 'observationModule')) {
            if (Object.prototype.hasOwnProperty.call(feSettingsJson.observationModule, 'meMode')) {
                observationModule.meMode = feSettingsJson.observationModule.meMode
            }
            if (Object.prototype.hasOwnProperty.call(feSettingsJson.observationModule, 'sort')) {
                observationModule.sort = setObservationSort(feSettingsJson.observationModule.sort)
            }
            if (Object.prototype.hasOwnProperty.call(feSettingsJson.observationModule, 'filter')) {
                observationModule.filter = setObservationFilter(
                    feSettingsJson.observationModule.filter
                )
            }
        }
    }

    return observationModule
}

export function filterToParams(
    sorting: Record<string, any>,
    filters: Record<string, any>,
    type: 'search' | 'export',
    level: 'project' | 'projectGroup',
    meMode: boolean,
    currentUserId: null | string
) {
    let sortString = Object.values(sorting)
        .filter(Boolean)
        .join(',')
        .replace('id', 'projectObservationId')

    const timeZone = moment.tz.guess(true)

    const params = {
        ...(filters.searchTerm && {
            'filter[searchTerm]': filters.searchTerm,
        }),
        ...(filters.priority.includes('null') && {
            'filter[priority][isNull]': true,
        }),
        ...(!filters.priority.includes('null') &&
            filters.priority.length > 0 && {
                'filter[priority]': filters.priority.join(),
            }),
        ...(filters.severity.includes('null') && {
            'filter[severity][isNull]': true,
        }),
        ...(!filters.severity.includes('null') &&
            filters.severity.length > 0 && {
                'filter[severity]': filters.severity.join(),
            }),

        ...(filters.dueStatus &&
            filters.dueStatus.length > 0 && {
                'filter[dueStatus]': filters.dueStatus.join(),
            }),
        ...(filters.archived !== null && {
            'filter[archived]': filters.archived,
        }),
        ...(filters.watching !== null && {
            'filter[watching]': filters.watching,
        }),
        ...(filters.reporterIsNull !== null && {
            'filter[reporter][isNull]': filters.reporterIsNull,
        }),
        ...(filters.reporter && {
            'filter[reporter]': filters.reporter,
        }),

        ...(filters.reporterCompany.includes('null') && {
            'filter[reporterCompany][isNull]': true,
        }),
        ...(!filters.reporterCompany.includes('null') &&
            filters.reporterCompany.length > 0 && {
                'filter[reporterCompany]': filters.reporterCompany.join(),
            }),

        ...((filters?.createdAt?.period || filters?.createdAt?.ge) && {
            'filter[createdAt][ge]': filters?.createdAt?.period
                ? moment(new Date(Date.now() - 86400 * 1000 * filters.createdAt.period))
                      .tz(timeZone)
                      .format()
                : filters.createdAt.ge,
        }),
        ...((filters?.createdAt?.period || filters?.createdAt?.le) && {
            'filter[createdAt][le]': filters?.createdAt?.period
                ? moment(new Date(Date.now())).tz(timeZone).format()
                : filters.createdAt.le,
        }),
        ...((filters?.closedAt?.period || filters?.closedAt?.ge) && {
            'filter[closedAt][ge]': filters?.closedAt?.period
                ? moment(new Date(Date.now() - 86400 * 1000 * filters.closedAt.period))
                      .tz(timeZone)
                      .format()
                : filters.closedAt.ge,
        }),
        ...((filters?.closedAt?.period || filters?.closedAt?.le) && {
            'filter[closedAt][le]': filters?.closedAt?.period
                ? moment(new Date(Date.now())).tz(timeZone).format()
                : filters.closedAt.le,
        }),

        ...((filters?.publishedAt?.period || filters?.publishedAt?.ge) && {
            'filter[publishedAt][ge]': filters?.publishedAt?.period
                ? moment(new Date(Date.now() - 86400 * 1000 * filters.publishedAt.period))
                      .tz(timeZone)
                      .format()
                : filters.publishedAt.ge,
        }),
        ...((filters?.publishedAt?.period || filters?.publishedAt?.le) && {
            'filter[publishedAt][le]': filters?.publishedAt?.period
                ? moment(new Date(Date.now())).tz(timeZone).format()
                : filters.publishedAt.le,
        }),

        ...((filters?.updatedAt?.period || filters?.updatedAt?.ge) && {
            'filter[updatedAt][ge]': filters?.updatedAt?.period
                ? moment(new Date(Date.now() - 86400 * 1000 * filters.updatedAt.period))
                      .tz(timeZone)
                      .format()
                : filters.updatedAt.ge,
        }),
        ...((filters?.updatedAt?.period || filters?.updatedAt?.le) && {
            'filter[updatedAt][le]': filters?.updatedAt?.period
                ? moment(new Date(Date.now())).tz(timeZone).format()
                : filters.updatedAt.le,
        }),

        ...((filters?.happenedAt?.period || filters?.happenedAt?.ge) && {
            'filter[happenedAt][ge]': filters?.happenedAt?.period
                ? moment(new Date(Date.now() - 86400 * 1000 * filters.happenedAt.period))
                      .tz(timeZone)
                      .format()
                : filters.happenedAt.ge,
        }),
        ...((filters?.happenedAt?.period || filters?.happenedAt?.le) && {
            'filter[happenedAt][le]': filters?.happenedAt?.period
                ? moment(new Date(Date.now())).tz(timeZone).format()
                : filters.happenedAt.le,
        }),

        ...(filters?.cost?.ge && {
            'filter[cost][ge]': filters.cost.ge,
        }),
        ...(filters?.cost?.le && {
            'filter[cost][le]': filters.cost.le,
        }),
        ...(sortString.length > 0 && { sort: sortString }),
    }

    if (level == 'project') {
        if (filters.contractors.includes('null')) {
            params['filter[contractors][isEmpty]'] = true
        } else if (!filters.contractors.includes('null') && filters.contractors.length > 0) {
            params['filter[contractors][contains]'] = filters.contractors.join()
        }

        if (filters.category.includes('null')) {
            params['filter[category][isNull]'] = true
        } else if (!filters.category.includes('null') && filters.category.length > 0) {
            params['filter[category]'] = filters.category.join()
        }

        if (filters.cause.includes('null')) {
            params['filter[cause][isNull]'] = true
        } else if (!filters.cause.includes('null') && filters.cause.length > 0) {
            params['filter[cause]'] = filters.cause.join()
        }

        if (filters.location.includes('null')) {
            params['filter[location][isNull]'] = true
        } else if (!filters.location.includes('null') && filters.location.length > 0) {
            params['filter[location]'] = filters.location.join()
        }
    } else if (level == 'projectGroup') {
        if (filters.relatedCompanies.includes('null')) {
            params['filter[contractors][isEmpty]'] = true
        } else if (
            !filters.relatedCompanies.includes('null') &&
            filters.relatedCompanies.length > 0
        ) {
            params['filter[relatedCompanies][contains]'] = filters.relatedCompanies.join()
        }

        if (filters.locationName.includes('null')) {
            params['filter[location][isNull]'] = true
        } else if (!filters.locationName.includes('null') && filters.locationName.length > 0) {
            params['filter[location.name]'] = filters.locationName.join()
        }

        if (filters.causeName.includes('null')) {
            params['filter[cause][isNull]'] = true
        } else if (!filters.causeName.includes('null') && filters.causeName.length > 0) {
            params['filter[cause.name]'] = filters.causeName.join()
        }

        if (filters.categoryName.includes('null')) {
            params['filter[category][isNull]'] = true
        } else if (!filters.categoryName.includes('null') && filters.categoryName.length > 0) {
            params['filter[category.name]'] = filters.categoryName.join()
        }

        if (filters?.project?.length > 0) {
            params['filter[project]'] = filters.project.join()
        }
    }

    if (type == 'export') {
        params['filter[visibility]'] = 'public'
    } else if (type == 'search') {
        if (filters?.visibility?.length > 0)
            params['filter[visibility]'] = filters.visibility.join()
    }

    if (meMode) {
        params['filter[status]'] = 'open,inProgress'
        if (currentUserId) params['filter[responsibleUserOrLeader]'] = currentUserId
    } else {
        if (filters?.status?.length > 0) params['filter[status]'] = filters.status.join()

        if (filters.responsibleUser.includes('null')) {
            params['filter[responsibleUser][isNull]'] = true
        } else if (
            !filters.responsibleUser.includes('null') &&
            filters.responsibleUser.length > 0
        ) {
            params['filter[responsibleUser]'] = filters.responsibleUser.join()
        }

        if (filters.responsibleLeader.includes('null')) {
            params['filter[responsibleLeader][isNull]'] = true
        } else if (
            !filters.responsibleLeader.includes('null') &&
            filters.responsibleLeader.length > 0
        ) {
            params['filter[responsibleLeader]'] = filters.responsibleLeader.join()
        }
    }

    return params
}

// Observation Statistics
export const aggregateFunction = ['count', 'avg', 'min', 'max', 'sum'] as const

export const aggregateField = ['cost', 'timePublishedToClosed'] as const

export const aggregatePeriod = [null, 'day', 'week', 'month', 'quarter', 'year'] as const

export const aggregatePeriodSource = ['happenedAt', 'publishedAt', 'updatedAt', 'closedAt'] as const

export const groupByProject = [
    'status',
    'priority',
    'severity',
    'dueStatus',
    'reporter',
    'reporterRole',
    'reporterCompany',
    'reporterCompanyRole',
    'responsibleUser',
    'responsibleLeader',
    'contractors',
    'category',
    'cause',
    'location',
] as const

export const groupByProjectGroup = [
    'status',
    'priority',
    'severity',
    'dueStatus',
    'reporter',
    'reporterRole',
    'reporterCompany',
    'reporterCompanyRole',
    'responsibleUser',
    'responsibleLeader',
    'relatedCompanies',
    'project',
    'categoryName',
    'causeName',
    'locationName',
] as const

export const numberFormat = ['number', 'percent'] as const

export type NumberFormat = (typeof numberFormat)[number]

// Observation Statistics Filters
export const observationStatisticsSingleValues = [
    'aggregateFunction',
    'aggregateField',
    'aggregatePeriodSource',
    'project',
    'projectGroup',
    'reporterIsNull',
]

export const observationStatisticsArrayValues = [
    'project',
    'reporter',
    'status',
    'priority',
    'severity',
    'dueStatus',
    'category',
    'categoryName',
    'cause',
    'causeName',
    'location',
    'locationName',
    'responsibleUser',
    'responsibleLeader',
    // 'reporterCompany',
    // 'reporterCompanyRole',
    'relatedCompanies',
    'contractors',
]

export const observationStatisticsObjectValues = [
    'happenedAt',
    'publishedAt',
    'updatedAt',
    'closedAt',
]

export const observationStatisticsFilters = {
    aggregateFunction: 'count',
    aggregateField: 'timePublishedToClosed',
    aggregatePeriod: null,
    aggregatePeriodSource: 'happenedAt',
    groupBy: null,
    project: null,
    projectGroup: null,
    reporter: [],
    reporterIsNull: null,
    status: [],
    priority: [],
    severity: [],
    dueStatus: [],
    category: [],
    categoryName: [],
    cause: [],
    causeName: [],
    location: [],
    locationName: [],
    responsibleUser: [],
    responsibleLeader: [],
    reporterCompany: [],
    reporterCompanyRole: [],
    relatedCompanies: [],
    contractors: [],
    happenedAt: {
        ge: null,
        le: null,
        period: null,
    },
    publishedAt: {
        ge: null,
        le: null,
        period: null,
    },
    updatedAt: {
        ge: null,
        le: null,
        period: null,
    },
    closedAt: {
        ge: null,
        le: null,
        period: null,
    },
}

export function setObservationStatisticsFilters(filters: Record<string, any>) {
    const copyObservationStatisticsFilters = cloneDeep(observationStatisticsFilters)

    Object.keys(copyObservationStatisticsFilters).map((key) => {
        if (filters[key] == undefined) return

        if (observationStatisticsSingleValues.includes(key)) {
            if (filters[key] != null) {
                copyObservationStatisticsFilters[key] = filters[key]
            }
        } else if (observationStatisticsArrayValues.includes(key)) {
            if (isArray(filters[key])) {
                if (filters[key]?.length > 0) {
                    copyObservationStatisticsFilters[key] = filters[key]
                }
            } else {
                if (filters[key] != null) {
                    copyObservationStatisticsFilters[key] = [filters[key]]
                }
            }
        } else if (observationStatisticsObjectValues.includes(key)) {
            Object.keys(filters[key]).map((objKey) => {
                if (filters[key][objKey] != null) {
                    copyObservationStatisticsFilters[key][objKey] = filters[key][objKey]
                }
            })
        }
    })
    return copyObservationStatisticsFilters
}

export function findActiveObservationStatisticsFilters(
    filters: Record<string, any> | null,
    selectableFilters = [] as Array<string>
) {
    if (!filters) return []
    const addedFilters = [] as Array<string>
    Object.keys(filters).map((key) => {
        if (!selectableFilters.includes(key)) return
        if (observationStatisticsSingleValues.includes(key)) {
            if (filters[key] != null) {
                addedFilters.push(key)
            }
        } else if (observationStatisticsArrayValues.includes(key)) {
            if (isArray(filters[key]) && filters[key]?.length > 0) {
                addedFilters.push(key)
            }
        } else if (observationStatisticsObjectValues.includes(key)) {
            Object.keys(filters[key]).map((objKey) => {
                if (filters[key][objKey] != null) {
                    addedFilters.push(key)
                }
            })
        }
    })
    return addedFilters
}

export function sortFilters(filters: Array<string>) {
    const order = [
        'status',
        'severity',
        'priority',
        'category',
        'categoryName',
        'dueStatus',
        'responsibleUser',
        'responsibleLeader',
        'relatedCompanies',
        'contractors',
        'happenedAt',
        'cause',
        'causeName',
        'location',
        'locationName',
    ]

    // Filters that exist in the `order` array
    const orderedFilters = filters.filter((filter) => order.includes(filter))

    // Filters that do not exist in the `order` array
    const extraFilters = filters.filter((filter) => !order.includes(filter))

    // Sort the `orderedFilters` based on their position in `order`
    orderedFilters.sort((a, b) => order.indexOf(a) - order.indexOf(b))

    // Combine the sorted filters with the extra filters
    return [...orderedFilters, ...extraFilters]
}
